import React from "react";
import { styles } from "../../styles/styles.js";
import ReactMarkdown from "react-markdown";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";

// Leagues where spread/total lines are fixed and movement is in the price
const TIGHT_LINE_LEAGUES = ["NHL", "MLB", "EPL", "MLS"];

// Convert American odds to implied probability
const oddsToImpliedProbability = (odds) => {
  if (odds > 0) {
    return (100 / (odds + 100)) * 100;
  } else {
    return (Math.abs(odds) / (Math.abs(odds) + 100)) * 100;
  }
};

// Detect line changes in a history array.
//
// Pairs that straddle a change of sportsbook are skipped. Two books quote
// different numbers, so `bovada -3.0` followed by `lowvig -3.5` is not the line
// moving half a point -- it is a different book's opinion, and annotating it as a
// market move asserts something untrue. See detectBookChanges below.
const detectLineChanges = (entries, lineKey) => {
  const changes = [];
  for (let i = 1; i < entries.length; i++) {
    if (entries[i - 1].bookmaker !== entries[i].bookmaker) continue;
    const prev = entries[i - 1][lineKey];
    const curr = entries[i][lineKey];
    if (prev !== undefined && curr !== undefined && prev !== curr) {
      changes.push({ update: i + 1, from: prev, to: curr });
    }
  }
  return changes;
};

// Where the series stops being one book's opinion and becomes another's.
//
// `line_history` is append-only and multi-book by design: the pipeline re-selects
// the tightest book each poll, and it changes when a book stops quoting a market
// or a tighter one appears. Measured 2026-08-11, 37% of MLB moneyline steps were
// a book change. Drawing a continuous line across one is a chart that asserts a
// market move that never occurred.
const detectBookChanges = (entries) => {
  const changes = [];
  for (let i = 1; i < entries.length; i++) {
    if (entries[i - 1].bookmaker !== entries[i].bookmaker) {
      changes.push({
        at: i + 0.5,
        from: entries[i - 1].bookmaker,
        to: entries[i].bookmaker,
      });
    }
  }
  return changes;
};

// Build chart rows, inserting a null-valued spacer at every book change.
//
// Recharts leaves `connectNulls` false by default, so a row whose values are null
// splits the path in two while keeping both real points on the axis. The spacer
// sits at a half index so the integer "Update #" ticks still line up with real
// updates.
const withBookBreaks = (entries, toRow) => {
  const rows = [];
  entries.forEach((entry, i) => {
    if (i > 0 && entries[i - 1].bookmaker !== entry.bookmaker) {
      const blank = { update: i + 0.5 };
      Object.keys(toRow(entry, i)).forEach((key) => {
        if (key !== "update") blank[key] = null;
      });
      rows.push(blank);
    }
    rows.push(toRow(entry, i));
  });
  return rows;
};

const bookLabel = (book) => book || "unknown";

const fontFamily =
  "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace";

const titleStyle = {
  color: "white",
  marginBottom: "20px",
  fontSize:
    "clamp(12px, calc(10px + (4) * ((100vw - 300px) / (1200 - 320))), 20px)",
  fontFamily,
};

const tickStyle = {
  fill: "white",
  fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
  fontFamily,
};

const badgeStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.3)",
  color: "rgba(255,255,255,0.7)",
  fontSize: "0.7rem",
  fontFamily,
  marginLeft: "10px",
};

const changeAnnotationStyle = {
  color: "#FFD700",
  fontSize: "0.75rem",
  fontFamily,
  marginTop: "8px",
  padding: "4px 8px",
};

const LineMovement = ({ fixture }) => {
  if (!fixture.line_history) {
    return null;
  }
  const hasMoneylineData =
    fixture.line_history.moneyline &&
    fixture.line_history.moneyline.length > 1;
  const hasSpreadData =
    fixture.line_history.spread && fixture.line_history.spread.length > 1;
  const hasTotalsData =
    fixture.line_history.totals && fixture.line_history.totals.length > 1;

  if (!hasMoneylineData && !hasSpreadData && !hasTotalsData) {
    return null;
  }

  const isTightLine = TIGHT_LINE_LEAGUES.includes(fixture.league);
  const chartHeight = styles.height || 300;
  const makeTicks = (len) => Array.from({ length: len + 3 }, (_, i) => i + 1);

  // Build chart data arrays with update index
  const moneylineEntries = hasMoneylineData ? fixture.line_history.moneyline : [];
  const moneylineData = withBookBreaks(moneylineEntries, (entry, i) => ({
    update: i + 1,
    home: parseFloat(oddsToImpliedProbability(entry.home).toFixed(1)),
    away: parseFloat(oddsToImpliedProbability(entry.away).toFixed(1)),
  }));
  const moneylineBookChanges = detectBookChanges(moneylineEntries);

  // Spread data — price-based implied probability for tight-line leagues
  let spreadData = [];
  let spreadLineChanges = [];
  let spreadBookChanges = [];
  let spreadCurrentLine = null;
  if (hasSpreadData) {
    const entries = fixture.line_history.spread;
    spreadBookChanges = detectBookChanges(entries);
    if (isTightLine && entries[0].home_price !== undefined) {
      spreadData = withBookBreaks(entries, (entry, i) => ({
        update: i + 1,
        home: parseFloat(oddsToImpliedProbability(entry.home_price).toFixed(1)),
        away: parseFloat(oddsToImpliedProbability(entry.away_price).toFixed(1)),
      }));
      spreadLineChanges = detectLineChanges(entries, "home_line");
      spreadCurrentLine = entries[entries.length - 1].home_line;
    } else {
      spreadData = withBookBreaks(entries, (entry, i) => ({
        update: i + 1,
        home: -entry.home_line,
        away: -entry.away_line,
      }));
    }
  }

  // Totals data — price-based implied probability for tight-line leagues
  let totalsData = [];
  let totalsLineChanges = [];
  let totalsBookChanges = [];
  let totalsCurrentLine = null;
  if (hasTotalsData) {
    const entries = fixture.line_history.totals;
    totalsBookChanges = detectBookChanges(entries);
    if (isTightLine && entries[0].over_price !== undefined) {
      totalsData = withBookBreaks(entries, (entry, i) => ({
        update: i + 1,
        over: parseFloat(oddsToImpliedProbability(entry.over_price).toFixed(1)),
        under: parseFloat(oddsToImpliedProbability(entry.under_price).toFixed(1)),
      }));
      totalsLineChanges = detectLineChanges(entries, "over");
      totalsCurrentLine = entries[entries.length - 1].over;
    } else {
      totalsData = withBookBreaks(entries, (entry, i) => ({
        update: i + 1,
        total: entry.over,
      }));
    }
  }

  // Reusable renderers for the book boundary: a dashed marker on the chart and a
  // line of prose under it naming both books. Shown on every chart that has one.
  const bookChangeMarkers = (changes) =>
    changes.map((change, i) => (
      <ReferenceLine
        key={`book-${i}`}
        x={change.at}
        stroke="#FFD700"
        strokeDasharray="4 4"
      />
    ));

  const bookChangeNotes = (changes) =>
    changes.map((change, i) => (
      <div key={`booknote-${i}`} style={changeAnnotationStyle}>
        Sportsbook changed: {bookLabel(change.from)} → {bookLabel(change.to)}. Prices
        before and after are not comparable — different books price the same market
        differently, so the gap is not a market move.
      </div>
    ));

  const bookBadge = (book) =>
    book ? <span style={badgeStyle}>odds: {bookLabel(book)}</span> : null;

  const currentBook = (entries) =>
    entries.length ? entries[entries.length - 1].bookmaker : null;

  const tooltipStyle = {
    contentStyle: { backgroundColor: "#1a1a1a", border: "1px solid #333", fontFamily },
    labelStyle: { color: "white" },
    itemStyle: { fontFamily },
  };

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Moneyline Chart */}
      {hasMoneylineData && (
        <div style={{ marginBottom: "40px" }}>
          <h5 style={titleStyle}>
            Moneyline (Implied Probability %)
            {bookBadge(currentBook(moneylineEntries))}
          </h5>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={moneylineData}>
              <CartesianGrid
                stroke="rgba(255, 255, 255, 0.2)"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="update"
                type="number"
                domain={[1, moneylineEntries.length + 3]}
                ticks={makeTicks(moneylineEntries.length)}
                label={{ value: "Update #", position: "insideBottom", offset: -5, style: tickStyle }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <YAxis
                label={{ value: "Implied Probability (%)", angle: -90, position: "insideLeft", offset: 10, style: tickStyle }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <Tooltip {...tooltipStyle} />
              <Legend
                wrapperStyle={{ ...tickStyle, paddingTop: "10px" }}
              />
              <Line
                type="linear"
                dataKey="home"
                name={fixture.home_team}
                stroke="#20B2AA"
                dot={false}
                strokeWidth={2}
              />
              <Line
                type="linear"
                dataKey="away"
                name={fixture.away_team}
                stroke="red"
                dot={false}
                strokeWidth={2}
              />
              {bookChangeMarkers(moneylineBookChanges)}
            </LineChart>
          </ResponsiveContainer>
          {bookChangeNotes(moneylineBookChanges)}
        </div>
      )}

      {/* Spread Chart */}
      {hasSpreadData && (
        <div style={{ marginBottom: "40px" }}>
          <h5 style={titleStyle}>
            {isTightLine && spreadCurrentLine !== null
              ? "Spread Price Movement (Implied Probability %)"
              : "Spread (positive = favored)"}
            {isTightLine && spreadCurrentLine !== null && (
              <span style={badgeStyle}>
                Line: {spreadCurrentLine > 0 ? "+" : ""}{spreadCurrentLine}
              </span>
            )}
            {bookBadge(currentBook(fixture.line_history.spread))}
          </h5>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={spreadData}>
              <CartesianGrid
                stroke="rgba(255, 255, 255, 0.2)"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="update"
                type="number"
                domain={[1, fixture.line_history.spread.length + 3]}
                ticks={makeTicks(fixture.line_history.spread.length)}
                label={{ value: "Update #", position: "insideBottom", offset: -5, style: tickStyle }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <YAxis
                label={{
                  value: isTightLine && spreadCurrentLine !== null
                    ? "Implied Probability (%)"
                    : "Spread (points)",
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: tickStyle,
                }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <Tooltip {...tooltipStyle} />
              <Legend
                wrapperStyle={{ ...tickStyle, paddingTop: "10px" }}
              />
              <Line
                type="linear"
                dataKey="home"
                name={fixture.home_team}
                stroke="#20B2AA"
                dot={false}
                strokeWidth={2}
              />
              <Line
                type="linear"
                dataKey="away"
                name={fixture.away_team}
                stroke="red"
                dot={false}
                strokeWidth={2}
              />
              {spreadLineChanges.map((c, i) => (
                <ReferenceLine
                  key={`spread-change-${i}`}
                  x={c.update}
                  stroke="#FFD700"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                />
              ))}
              {bookChangeMarkers(spreadBookChanges)}
            </LineChart>
          </ResponsiveContainer>
          {spreadLineChanges.length > 0 && (
            <div style={changeAnnotationStyle}>
              {spreadLineChanges.map((c, i) => (
                <div key={i}>
                  Line changed at update {c.update}: {c.from > 0 ? "+" : ""}{c.from} → {c.to > 0 ? "+" : ""}{c.to}
                </div>
              ))}
            </div>
          )}
          {bookChangeNotes(spreadBookChanges)}
        </div>
      )}

      {/* Totals Chart */}
      {hasTotalsData && (() => {
        let totalsYMin, totalsYMax;
        if (isTightLine && totalsCurrentLine !== null) {
          const allVals = totalsData.flatMap(d => [d.over, d.under]).filter(v => v != null);
          totalsYMin = Math.max(0, Math.floor(Math.min(...allVals) - 10));
          totalsYMax = Math.min(100, Math.ceil(Math.max(...allVals) + 10));
        } else {
          const allVals = totalsData.map(d => d.total).filter(v => v != null);
          totalsYMin = Math.floor(Math.min(...allVals) - 10);
          totalsYMax = Math.ceil(Math.max(...allVals) + 10);
        }
        return (
        <div style={{ marginBottom: "40px" }}>
          <h5 style={titleStyle}>
            {isTightLine && totalsCurrentLine !== null
              ? "Totals Price Movement (Implied Probability %)"
              : "Totals (Over/Under Line)"}
            {isTightLine && totalsCurrentLine !== null && (
              <span style={badgeStyle}>
                Line: {totalsCurrentLine}
              </span>
            )}
            {bookBadge(currentBook(fixture.line_history.totals))}
          </h5>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={totalsData}>
              <CartesianGrid
                stroke="rgba(255, 255, 255, 0.2)"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="update"
                type="number"
                domain={[1, fixture.line_history.totals.length + 3]}
                ticks={makeTicks(fixture.line_history.totals.length)}
                label={{ value: "Update #", position: "insideBottom", offset: -5, style: tickStyle }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <YAxis
                domain={[totalsYMin, totalsYMax]}
                label={{
                  value: isTightLine && totalsCurrentLine !== null
                    ? "Implied Probability (%)"
                    : "Total Points",
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: tickStyle,
                }}
                tick={{ style: tickStyle }}
                stroke="white"
              />
              <Tooltip {...tooltipStyle} />
              {isTightLine && totalsCurrentLine !== null ? (
                <>
                  <Legend wrapperStyle={{ ...tickStyle, paddingTop: "10px" }} />
                  <Line
                    type="linear"
                    dataKey="over"
                    name="Over"
                    stroke="#FF6347"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="linear"
                    dataKey="under"
                    name="Under"
                    stroke="#20B2AA"
                    dot={false}
                    strokeWidth={2}
                      />
                  {totalsLineChanges.map((c, i) => (
                    <ReferenceLine
                      key={`totals-change-${i}`}
                      x={c.update}
                      stroke="#FFD700"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                    />
                  ))}
                </>
              ) : (
                <Line
                  type="linear"
                  dataKey="total"
                  name="Total"
                  stroke="#FF6347"
                  dot={false}
                  strokeWidth={2}
                />
              )}
              {bookChangeMarkers(totalsBookChanges)}
            </LineChart>
          </ResponsiveContainer>
          {totalsLineChanges.length > 0 && (
            <div style={changeAnnotationStyle}>
              {totalsLineChanges.map((c, i) => (
                <div key={i}>
                  Line changed at update {c.update}: {c.from} → {c.to}
                </div>
              ))}
            </div>
          )}
          {bookChangeNotes(totalsBookChanges)}
        </div>
        );
      })()}

      {fixture.box_score && fixture.box_score['line_movement'] && (
        <div style={{ marginTop: '20px' }}>
          <ul style={{
            listStyleType: 'none',
            padding: 0,
            margin: 0,
            fontSize: 'clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)'
          }}>
            <ReactMarkdown>{fixture.box_score['line_movement']}</ReactMarkdown>
          </ul>
        </div>
      )}
    </div>
  );
};

export default LineMovement;
